import { ParsedUrlQuery } from 'querystring';
import { ClientSafeTenantConfig } from 'server/config-cache/types';
import { FunnelCart } from 'src/ts/types/cart';
import { Plan } from 'src/services/cart-order/plans-service';
import { PlainFilters, PlainFiltersSchema } from './pre-selected-filters';

/**
 * These are functions that transform from various different data sources that contain filters
 * into the common PlainFilters type which are passed into the above function to be used as
 * initially set filters.
 */
function validatePlainFiltersSchema(plainFilters: PlainFilters): PlainFilters {
  try {
    const validatedFilters = PlainFiltersSchema.parse(plainFilters);
    return validatedFilters;
  } catch (error) {
    console.error('Invalid filter params:', error);
    return {};
  }
}

export function tenantConfigToFilters(
  tenantConfig: ClientSafeTenantConfig,
): PlainFilters {
  const preselectedFilters = {
    tagIds: tenantConfig.visibleTagFilters?.length
      ? tenantConfig.visibleTagFilters
          .filter((visibleTagFilter) => visibleTagFilter.preselected)
          .map((visibleTagFilter) => visibleTagFilter.tag.id)
      : undefined,
  };

  return validatePlainFiltersSchema(preselectedFilters);
}

export function funnelCartParamsToFilters(
  funnelCart: FunnelCart,
  queryParams?: ParsedUrlQuery,
): PlainFilters {
  if (queryParams?.filterMode === 'ignore-cart') {
    return {};
  }

  const preselectedFilters = {
    tagIds: funnelCart.funnelData['filters']?.['tagIds']?.length
      ? funnelCart.funnelData['filters']['tagIds']
      : undefined,
    ingredientToAvoidIds: funnelCart.funnelData['filters']?.[
      'ingredientToAvoidIds'
    ]?.length
      ? funnelCart.funnelData['filters']?.['ingredientToAvoidIds']
      : undefined,
    macros: funnelCart.funnelData['filters']?.['macros']
      ? Object.keys(funnelCart.funnelData['filters']?.['macros']).map(
          (macroKey) => {
            return {
              key: macroKey,
              min: funnelCart.funnelData['filters']?.['macros'][macroKey].min,
              max: funnelCart.funnelData['filters']?.['macros'][macroKey].max,
            };
          },
        )
      : undefined,
  };

  return validatePlainFiltersSchema(preselectedFilters);
}

export function planToFilters(plan: Plan): PlainFilters {
  const { mealConfig } = plan;
  const { nutritionalFacts } = mealConfig;

  // Extract macro keys by finding all "Min" suffixed fields on nutritionalFacts.
  // The plan uses "caloriesInKcal" while the filter system uses "calories".
  const macroKeyOverrides: Record<string, string> = {
    caloriesInKcal: 'calories',
  };

  const macros = Object.keys(nutritionalFacts)
    .filter((key) => key.endsWith('Min'))
    .map((minKey) => {
      const factKey = minKey.replace(/Min$/, '');
      const macroKey = macroKeyOverrides[factKey] ?? factKey;
      const min = nutritionalFacts[
        minKey as keyof typeof nutritionalFacts
      ] as number;
      const max = nutritionalFacts[
        `${factKey}Max` as keyof typeof nutritionalFacts
      ] as number | null;

      return { key: macroKey, min, max };
    });

  const preselectedFilters: PlainFilters = {
    tagIds: mealConfig.tagIds?.length ? mealConfig.tagIds : undefined,
    ingredientToAvoidIds: mealConfig.ingredientToAvoidIds?.length
      ? mealConfig.ingredientToAvoidIds
      : undefined,
    macros: macros.length ? macros : undefined,
  };

  return validatePlainFiltersSchema(preselectedFilters);
}

export function urlParamsToFilters(queryParams: ParsedUrlQuery): PlainFilters {
  let tagIds: number[] | undefined = undefined;
  if (Array.isArray(queryParams.tagId)) {
    tagIds = queryParams.tagId.map((id) => Number(id));
  } else if (queryParams.tagId) {
    tagIds = [Number(queryParams.tagId)];
  }

  let ingredientToAvoidIds: number[] | undefined = undefined;
  if (Array.isArray(queryParams.ingredientToAvoidId)) {
    ingredientToAvoidIds = queryParams.ingredientToAvoidId.map((id) =>
      Number(id),
    );
  } else if (queryParams.ingredientToAvoidId) {
    ingredientToAvoidIds = [Number(queryParams.ingredientToAvoidId)];
  }

  let chefNames: string[] | undefined = undefined;
  if (Array.isArray(queryParams.chefName)) {
    chefNames = queryParams.chefName as string[];
  } else if (queryParams.chefName) {
    chefNames = [queryParams.chefName as string];
  }

  const macroParams = Object.keys(queryParams).filter((queryParamKey) =>
    queryParamKey.endsWith('Macro'),
  );

  const macros: PlainFilters['macros'] | undefined = macroParams.length
    ? macroParams.map((queryParamKey) => {
        const [minStr, maxStr] = (queryParams[queryParamKey] as string).split(
          '~',
        );

        // maxStr can be null, this handles that case:
        const parsedMaxStr = maxStr === 'null' ? null : Number(maxStr);

        return {
          key: queryParamKey.split('Macro')[0],
          min: Number(minStr),
          max: parsedMaxStr,
        };
      })
    : undefined;

  return validatePlainFiltersSchema({
    tagIds,
    ingredientToAvoidIds,
    chefNames,
    macros,
  });
}
